'use client'

import { CSSProperties, useEffect, useMemo, useState } from 'react'
import {
	Button,
	cn,
	FilterSelect,
	Loader,
	ToggleButton,
} from '@desoutter/uikit'
import { CategoryRecord, IndustryRecord } from '@/graphql/types'
import { CatalogListWrapperFragment } from './CatalogListWrapper.generated'
import { getItems, GetItemsURLParams } from '@/lib/redpim'
import { ProductCard } from '@/components/Cards/ProductCard/ProductCard'
import ProductListItem from '@/components/Cards/ProductListItem/ProductListItem'
import { useParams } from 'next/navigation'
import { getSpecs } from '@/utils/getSpecsFromItemSpecificationBlocs'

import useSWRInfinite from 'swr/infinite'
import { useInView } from 'react-intersection-observer'
import {
	CatalogFilters,
	Filters,
	filtersSchema,
} from '@/components/Catalog/CatalogFilters/CatalogFilters'

const fetcher = async (params: GetItemsURLParams) => {
	const abortController = new AbortController()
	return await getItems({ ...params, active: ['active'] }, abortController)
}

type CatalogListWrapperProps = {
	fragment: CatalogListWrapperFragment
	categories: CategoryRecord[]
	industries: IndustryRecord[]
}

export default function CatalogListWrapper({
	fragment,
	categories,
	industries,
}: CatalogListWrapperProps) {
	const { lang } = useParams()

	const [filters, setFilters] = useState<Filters>({
		industries: [],
		types: [],
		categories: [],
		series: [],
		uses: [],
	})

	useEffect(() => {
		if (!window) return
		const result = filtersSchema.safeParse(
			Object.fromEntries(new URLSearchParams(window.location.search)),
		)
		if (result.success) setFilters(result.data)
	}, [])

	const params: GetItemsURLParams = useMemo(() => {
		return {
			categories: [...filters.series, ...filters.categories].join(','),
			uses: filters.uses.join(','),
			types: filters.types.join(','),
			industries: filters.industries.join(','),
		}
	}, [filters])

	const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
		(index) => ({
			...params,
			page: index,
			pageSize: 30,
		}),
		fetcher,
		{
			// prevent twice fetching with from 0
			revalidateFirstPage: false,
		},
	)

	const { ref } = useInView({
		onChange(inView) {
			if (inView && !isLoading) {
				setSize(size + 1)
			}
		},
		threshold: 0.5,
	})

	const displays = [
		{
			label: fragment.cardsButtonLabel,
			value: 'card',
		},
		{
			label: fragment.listButtonLabel,
			value: 'list',
		},
	]

	function onFilterChange(name: string, value: string) {
		const updateSearchParams = (filters: Record<string, string[]>) => {
			const searchParams = new URLSearchParams(window.location.search)
			Object.entries(filters).forEach(([key, value]) => {
				if (value.length) {
					searchParams.set(key, value.join(','))
				} else {
					searchParams.delete(key)
				}
			})
			history.replaceState(
				null,
				'',
				`${window.location.pathname}?${searchParams.toString()}`,
			)
		}

		const createNewFilters = (
			prev: Filters,
			name: string,
			value: string,
		): Filters => {
			if (value === 'all') {
				return { ...prev, [name]: [] }
			}
			const currentValues = prev[name] || []
			if (currentValues.includes(value)) {
				return {
					...prev,
					[name]: currentValues.filter((_value: string) => _value !== value),
				}
			}
			return { ...prev, [name]: [...currentValues, value] }
		}

		setFilters((prev) => {
			const newFilters = createNewFilters(prev, name, value)
			updateSearchParams(newFilters)
			return newFilters
		})
	}

	const [display, setDisplay] = useState<'card' | 'list'>('card')

	const items = useMemo(() => {
		if (!data) return []
		return [].concat(...data.map(({ items }) => items))
	}, [data])

	const total = useMemo(() => {
		return data ? data[data.length - 1]?.total : 0
	}, [data])

	return (
		<div className="relative container-auto py-8 md:py-24">
			<div
				className="grid [grid-template-areas:var(--grid-areas-mobile)] md:[grid-template-areas:var(--grid-areas-desktop)] md:grid-cols-[0.5fr_1.5fr] gap-6"
				style={
					{
						'--grid-areas-mobile': `
						"filters"
						"header"
						"main"
					`,
						'--grid-areas-desktop': `
						"header header"
						"filters main"
					`,
					} as CSSProperties
				}
			>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between [grid-area:header]">
					<div className="h4">
						{total}{' '}
						{total > 1
							? fragment.resultCountPluralLabel
							: fragment.resultCountSingularLabel}
					</div>
					<ToggleButton
						options={displays}
						value={display}
						onChange={setDisplay}
					/>
				</div>
				<div className="[grid-area:filters]">
					<div className="rounded-3xl">
						<div className="flex items-center justify-between">
							<div className="h5">{fragment.filtersTitle}</div>
							<Button variant="outline" title={fragment.resetButtonLabel}>
								{fragment.resetButtonLabel}
							</Button>
						</div>
						<CatalogFilters
							fragment={fragment}
							filters={filters}
							categories={categories}
							industries={industries}
							onFilterChange={onFilterChange}
						/>
					</div>
				</div>
				<div
					className={cn(
						'[grid-area:main] grid',
						'lg:min-w-[700px] 2xl:min-w-[800px]',
					)}
				>
					<div
						className={cn(
							'grid',
							display === 'card'
								? 'lg:grid-cols-2 2xl:grid-cols-3 gap-6'
								: 'gap-2',
							items.length > 0 && display === 'card' && 'md:w-fit md:ml-auto',
						)}
					>
						{!isLoading && !items.length && (
							<div className="text-center [grid-column:1/-1] text-xl font-light">
								No result
							</div>
						)}
						{items.map((item, i) =>
							display === 'list' ? (
								<ProductListItem
									partnumber={item.partnumber}
									model={item.model}
									imageUrl={
										!!item.thumbnail &&
										item.thumbnail +
											'/jcr:content/renditions/cq5dam.web.400.400.jpeg'
									}
									key={item.partnumber + i}
								/>
							) : (
								<ProductCard
									partnumber={item.partnumber}
									model={item.model}
									specs={getSpecs(item.specificationBlocks, lang as string)}
									imageUrl={
										!!item.thumbnail &&
										item.thumbnail +
											'/jcr:content/renditions/cq5dam.web.400.400.jpeg'
									}
									key={item.partnumber + i}
								/>
							),
						)}
						<div
							ref={ref}
							className="w-full flex justify-center items-center [grid-column:1/-1]"
						>
							{(isLoading || isValidating) && (
								<>
									<div className="flex justify-center">
										<Loader />
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
